import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  ResolvedSymbol,
  SourceItem,
  TrendingItem,
  TrendingSource,
  Watchlist,
} from "../api/types";
import EarningsBadge, { isEarningsWeek } from "../components/EarningsBadge";
import EmptyState from "../components/EmptyState";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import SourceBar from "../components/SourceBar";
import TopNav from "../components/TopNav";

type View = "all" | TrendingSource;

const VIEWS: { id: View; label: string }[] = [
  { id: "all", label: "All" },
  { id: "apewisdom", label: "Reddit/StockTwits" },
  { id: "stocktwits", label: "StockTwits live" },
  { id: "rss", label: "News mentions" },
];

const SOURCE_HINT: Record<View, string> = {
  all: "Aggregate of Apewisdom (Reddit + StockTwits), StockTwits trending, and ticker mentions across our news feeds. Refreshed every 30 min.",
  apewisdom: "Apewisdom — past-24h mention counts on r/wallstreetbets, r/stocks, r/investing and StockTwits.",
  stocktwits: "StockTwits — currently most-watched tickers (live trader chat).",
  rss: "Counts of ticker symbols (in our seeded universe) across the past 24h of news headlines.",
};

function useTrending(view: View) {
  return useQuery<TrendingItem[] | SourceItem[]>({
    queryKey: ["trending", view],
    queryFn: async () => {
      if (view === "all") {
        return (await api.get<TrendingItem[]>("/trending", { params: { limit: 30 } })).data;
      }
      return (
        await api.get<SourceItem[]>(`/trending/sources/${view}`, { params: { limit: 30 } })
      ).data;
    },
    refetchInterval: 60_000,
  });
}

function useWatchlist() {
  return useQuery<Watchlist>({
    queryKey: ["watchlist"],
    queryFn: async () => (await api.get<Watchlist>("/watchlist")).data,
    staleTime: 30_000,
  });
}

function ScoreNumber({ value, label }: { value: number; label: string }) {
  return (
    <div className="text-right">
      <div className="font-mono text-lg leading-none tabular-nums text-slate-100">
        {value.toFixed(value >= 100 ? 0 : 2)}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-slate-600">{label}</div>
    </div>
  );
}

function WatchToggle({
  onWatchlist,
  busy,
  errored,
  onClick,
}: {
  onWatchlist: boolean;
  busy: boolean;
  errored: boolean;
  onClick: () => void;
}) {
  if (onWatchlist) {
    return (
      <span
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30"
        title="On watchlist"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M16.704 5.296a1 1 0 0 1 0 1.414l-7.5 7.5a1 1 0 0 1-1.414 0l-3.5-3.5a1 1 0 1 1 1.414-1.414L8.5 12.086l6.79-6.79a1 1 0 0 1 1.414 0Z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    );
  }
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      disabled={busy}
      aria-label="Add to watchlist"
      title={errored ? "yfinance has no data for this ticker" : "Add to watchlist"}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full ring-1 transition disabled:opacity-50 ${
        errored
          ? "bg-rose-500/15 text-rose-300 ring-rose-500/30"
          : "bg-sky-500/15 text-sky-300 ring-sky-500/30 hover:bg-sky-500/30"
      }`}
    >
      {busy ? (
        <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 animate-spin" aria-hidden="true">
          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
          <path d="M17 10a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
          <path d="M10 4a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 10 4Z" />
        </svg>
      )}
    </button>
  );
}

function isTrending(view: View, _items: TrendingItem[] | SourceItem[]): _items is TrendingItem[] {
  return view === "all";
}

export default function Trending() {
  const qc = useQueryClient();
  const [view, setView] = useState<View>("all");
  const [earningsOnly, setEarningsOnly] = useState(false);
  const trending = useTrending(view);
  const wl = useWatchlist();

  const onWatchlist = new Set((wl.data?.items ?? []).map((i) => i.symbol.yahoo_symbol));

  // One unified "+" action: if the ticker is already cataloged, just add to
  // watchlist; otherwise resolve via yfinance first, then add. Errors keyed
  // by the row's ticker_text so the button can flip red on a per-row basis.
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});

  async function watchTicker(it: TrendingItem | SourceItem) {
    const key = it.ticker_text;
    setPending((p) => ({ ...p, [key]: true }));
    setErrors((e) => {
      const { [key]: _omit, ...rest } = e;
      return rest;
    });
    try {
      let yahoo = it.yahoo_symbol;
      if (!yahoo) {
        const r = (await api.post<ResolvedSymbol>("/symbols/resolve", { ticker_text: key })).data;
        yahoo = r.yahoo_symbol;
      }
      await api.post("/watchlist/items", { yahoo_symbol: yahoo, tier: "tracked" });
      qc.invalidateQueries({ queryKey: ["watchlist"] });
      qc.invalidateQueries({ queryKey: ["trending"] });
    } catch (err: any) {
      const msg =
        err?.response?.status === 404
          ? "yfinance has no data"
          : err?.response?.data?.detail || "failed";
      setErrors((e) => ({ ...e, [key]: msg }));
    } finally {
      setPending((p) => {
        const { [key]: _omit, ...rest } = p;
        return rest;
      });
    }
  }


  const allItems = trending.data ?? [];
  const items = earningsOnly && view === "all"
    ? (allItems as TrendingItem[]).filter((it) => isEarningsWeek(it.next_earnings_date))
    : allItems;
  const captured =
    items.length > 0 ? new Date(items[0].captured_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />

      <div className="mb-3 -mx-1 flex gap-1 overflow-x-auto px-1 pb-1 sm:mb-4">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              view === v.id
                ? "bg-slate-800 text-slate-100 ring-1 ring-slate-700"
                : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
            }`}
          >
            {v.label}
          </button>
        ))}
        {view === "all" && (
          <button
            onClick={() => setEarningsOnly((v) => !v)}
            title="Show only tickers reporting earnings within 7 days"
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              earningsOnly
                ? "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30"
                : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
            }`}
          >
            ⚡ Earnings this week
          </button>
        )}
      </div>

      <p className="mb-3 text-xs leading-relaxed text-slate-500 sm:mb-4">
        {SOURCE_HINT[view]}
        {captured && <span className="ml-1 text-slate-600">· last updated {captured}</span>}
      </p>

      {trending.isError && (
        <EmptyState
          variant="error"
          title="Couldn't load trending"
          hint="The Apewisdom or StockTwits source may be down. Auto-retrying every minute."
          action={
            <button
              onClick={() => trending.refetch()}
              className="rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:bg-rose-900/40"
            >
              Retry
            </button>
          }
        />
      )}
      {!trending.isError && trending.isLoading && (
        <EmptyState variant="loading" title="Loading…" />
      )}
      {!trending.isError && !trending.isLoading && items.length === 0 && (
        <EmptyState
          title="No tickers to show yet"
          hint={
            earningsOnly
              ? "No tickers with earnings within 7 days. Try clearing the earnings filter."
              : "Cron jobs run every 30 min — first snapshot may take a few minutes."
          }
        />
      )}
      {!trending.isError && !trending.isLoading && items.length > 0 && (
      <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
        <ul className="divide-y divide-slate-800">
          {items.map((it, i) => {
            const resolved = it.yahoo_symbol !== null;
            const onWl = resolved && onWatchlist.has(it.yahoo_symbol!);
            // Both cataloged and unmapped rows route to the detail page.
            // SymbolDetail auto-resolves on visit when the symbol isn't in
            // the catalog, so users can preview before deciding to watch.
            const detailHref = `/s/${encodeURIComponent(it.yahoo_symbol ?? it.ticker_text)}`;

            return (
              <li key={it.ticker_text}>
                <Link to={detailHref} className="block hover:bg-slate-800/40">
                  <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
                    <div className="w-6 shrink-0 pt-0.5 text-right text-xs font-medium tabular-nums text-slate-600">
                      {i + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-base text-slate-100">{it.ticker_text}</span>
                        {it.region && (
                          <span className="text-[10px] uppercase tracking-wide text-slate-600">
                            {it.region}
                          </span>
                        )}
                        {isTrending(view, items) && (
                          <EarningsBadge date={(it as TrendingItem).next_earnings_date} />
                        )}
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {it.name ?? <span className="italic text-slate-600">unmapped ticker · click to preview</span>}
                      </div>
                      {isTrending(view, items) && (it as TrendingItem).components && (
                        <div className="mt-2">
                          <SourceBar components={(it as TrendingItem).components} />
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {isTrending(view, items) ? (
                        <ScoreNumber value={(it as TrendingItem).score} label="score" />
                      ) : (
                        <ScoreNumber value={(it as SourceItem).raw_count} label="count" />
                      )}
                      <WatchToggle
                        onWatchlist={onWl}
                        busy={!!pending[it.ticker_text]}
                        errored={!!errors[it.ticker_text]}
                        onClick={() => watchTicker(it)}
                      />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
      )}

      <Footer note="Trending refreshes every 30 min · Tickers shown only if seeded in catalog" />
    </div>
  );
}
